import scrollIntoView from 'scroll-into-view';
import moment from './moment';

export const getWordCount = (value) => value?.length > 0 ? value.replaceAll(/[.,/#!$%^&*;:{}=\-_`~() ]+/g, ' ').trim().split(' ').length : 0;

export const formatDateTime = (value) => value ? moment(value).format('MM/DD/YYYY h:mm A') : 'unknown date';

export const nullishTernary = (value, positive, negative) => {
  if (value === null || value === undefined) {
    return value;
  }
  return value ? positive : negative;
};

export const isEmpty = (value) => {
  return value === null || value === undefined || value === '';
}

/**
 * Get a default value for radio buttons, which may be true, false, or null for no selection.
 */
export const maybeValue = (value) => {
  if (value !== null) {
    return value ? 'true' : 'false';
  }
  return null;
};

export const HTTP_ERRORS = [
  'Internal server error',
  'Bad Request'
];

/**
 * Merges status messages into a flat array.
 *
 * Can accept a string, an error object with 'message' and 'errors' properties, or an array containing a mix
 * of strings and error objects.
 *
 * @param msg
 * @returns {string[]}
 */
export const getArrayMsg = (msg) => {
  if (!msg) {
    return [];
  }

  if (!Array.isArray(msg)) {
    msg = [msg];
  }
  return msg.map(m => {
    if (!!m.errors || !!m.message) {
      // An error object was passed in. Collect strings from the 'message' and 'errors' properties.
      return [m.message, ...m.errors || []];
    }
    return m;
  }).flat().filter(m => !!m && !HTTP_ERRORS.includes(m));
};

export const toOption = (id, label = null) => ({ id, label: label || id });

/**
 * Helper to convert arrays in the form of [{value: 'foo'}] to options usable by <AutocompleteElement>
 */
export const toOptions = (opts) => opts.map(opt => {
  if (typeof opt === 'string') {
    return {
      id: opt,
      label: opt,
    };
  }
  if (opt.value) {
    return {
      id: opt.value,
      label: `${opt.label || opt.value}`,
    };
  }
  return opt;
});

/**
 * Format's a moment date/time into ISO standard as UTC without the conversion so that the
 * local time is pretended to be UTC all along.
 * @param m
 * @returns {string}
 */
export const notzUTCString = (m) => m.format('YYYY-MM-DDTHH:mm:ss.sss') + 'Z';

export const fullDateFormat = (mom) => mom.format('dddd, MMMM D YYYY | h:mm A');

/**
 * Sort an array of items based on the display_order property.
 * If no display_order is set, then a fallback attribute is used for string comparison.
 * @param items
 * @param fallbackAttr
 * @returns {*}
 */
export const sortDisplayOrder = (items = [], fallbackAttr = null) => {
  items.sort((a, b) => {
    if (a.display_order && b.display_order) {
      return a.display_order - b.display_order;
    } else if (a.display_order) {
      return -1;
    } else if (b.display_order) {
      return 1;
    } else if (fallbackAttr) {
      return a[fallbackAttr].localeCompare(b[fallbackAttr]);
    }
    return a - b;
  });
  return items;
};

/**
 * Sorts a list of options alphabetically by label. Use with array.sort().
 * e.g., `options.sort(sortByLabel)`
 */
export const sortByLabel = (a, b) => {
  if (!a.label || !b.label) {
    return 0;
  }
  return a.label.localeCompare(b.label);
}

/**
 * Basic object comparator that compares the properties of objects 'a' and 'b' specified by 'orderBy'
 *
 * @param a
 * @param b
 * @param orderBy
 * @returns {number}
 */
export const descendingComparator = (a, b, orderBy) =>  {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

/**
 * Return comparator based on string direction.
 *
 * @param order
 * @param orderBy
 * @returns {{(*, *): number, (*, *): number}}
 */
export const defaultComparator = (order, orderBy) =>  {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export const scrollToTitle = () => {
  setTimeout(() => {
    let elems = document.getElementsByClassName('page-title');
    if (elems.length > 0) {
      scrollIntoView(elems[0], { align: { top: 0 }});
    } else {
      window.scrollTo({ top: 200, left: 0, behavior: 'smooth' });
    }
  }, 100);
};

export const scrollToAlert = () => {
  setTimeout(() => {
    let root = document.getElementsByClassName('MuiModal-root')?.[0];
    let modal = true;
    if (!root) {
      modal = false;
      root = document;
    }
    let e = root.getElementsByClassName('alert-anchor')[0];
    if (e) {
      if (modal) {
        e.scrollIntoView({ behavior: 'smooth' });
      } else {
        scrollIntoView(e, { align: { top: 0 } });
      }
      setTimeout(() => e.focus({ preventScroll: true }), 100);
    } else {
      scrollToTitle();
    }
  }, 100);
};

export const capitalize = (str) => `${str.substring(0, 1).toUpperCase()}${str.substring(1)}`;

export const truncateString = (str, len = 25) => {
  if (!str) {
    return '';
  }
  return str.length > len ? `${str.substring(0, len)}...` : str;
}

export const last = (arr) => arr[arr.length - 1];

export const tenDigit = (phone) => {
  return phone.replace(/\+1/, '').replaceAll(/[^0-9]/g, '');
};

export const paletteMains = (theme) => Object.keys(theme.palette).reduce((acc, key) => {
  acc[key] = theme.palette[key].main;
  return acc;
}, {});

export const preventNonNumericKeyPress = (e) => {
  // <input type="number"> allows some mathematical chars like +-.e which we don't want.
  // This prevents users from entering those chars, while still allowing keys like
  // backspace, arrows, etc. to work.
  ['e', 'E', '+', '-', '.', ' '].includes(e.key) && e.preventDefault();
};

export const validatePositiveInteger = {
  validate: {
    integersOnly: (value) => {
      if (!!value && value !== Math.floor(value)) {
        return 'Please enter a whole number.';
      }
      return true;
    },
  },
  min: {
    value: 0,
    message: 'Please enter a positive number.'
  },
  max: {
    // Huge numbers cause API errors.
    value: 99999,
    message: 'Please enter a number below 100,000.'
  }
};

/**
 * Returns data received from the API in object form. Parses JSON strings, if necessary.
 */
export const maybeParseJson = (value) => {
  if (!value) {
    return {};
  }
  if (typeof value === 'string') {
    return JSON.parse(value);
  }
  return value;
}
